import React, { useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import Table from "../../components/common/Table";
import Modal from "../../components/common/Modal";
import { PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import TenantForm from "../../components/forms/TenantForm";
import {
  Tenant,
  TenantCreateFormData,
  TenantUpdateFormData,
} from "../../schemas/tenantSchema";
import { useTenantsInfinite } from "../../hooks/useTenantsInfinite";
import { useDebounce } from "../../hooks/useDebounce";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";
import { getChangedFields } from "../../utils/formUtils";

const TenantList: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const {
    tenants,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createTenant,
    updateTenant,
    deleteTenants,
  } = useTenantsInfinite({ search: debouncedSearch || undefined });
  const { canCreate, canDelete, canEdit, canView } =
    useAuthPermissions("tenants");

  const handleEdit = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setIsModalOpen(true);
  };

  const handleDelete = (tenant: Tenant) => {
    if (
      window.confirm(`Are you sure you want to delete ${tenant.companyName}?`)
    ) {
      deleteTenants([tenant.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (
      window.confirm(`Are you sure you want to delete ${ids.length} tenant(s)?`)
    ) {
      deleteTenants(ids);
      setRowSelection({});
    }
  };

  const handleSubmit = (data: TenantCreateFormData | TenantUpdateFormData) => {
    if (selectedTenant) {
      // Only send changed fields for update
      const changedFields = getChangedFields(data as TenantUpdateFormData, {
        companyName: selectedTenant.companyName,
        username: selectedTenant.username,
        email: selectedTenant.email,
        address: selectedTenant.address || "",
        isPremium: selectedTenant.isPremium,
        phoneNumber: selectedTenant.phoneNumber || "",
      });
      updateTenant({
        id: selectedTenant.id,
        tenant: changedFields,
      });
    } else {
      createTenant(data as TenantCreateFormData);
    }
    setIsModalOpen(false);
    setSelectedTenant(null);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedTenant(null);
  };

  const columns: ColumnDef<Tenant>[] = [
    { accessorKey: "companyName", header: "Company Name" },
    { accessorKey: "name", header: "Name" },
    { accessorKey: "email", header: "Email" },
    { accessorKey: "phoneNumber", header: "Phone Number" },
    { accessorKey: "address", header: "Address" },
    {
      accessorKey: "isPremium",
      header: "Premium",
      cell: ({ row }) => (row.original.isPremium ? "Yes" : "No"),
    },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
      visible: canEdit,
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
      visible: canDelete,
    },
  ];

  return (
    <div className="flex flex-col px-6 py-2">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold text-gray-800">Tenants</h1>
        {canCreate && (
          <button
            onClick={() => {
              setSelectedTenant(null);
              setIsModalOpen(true);
            }}
            className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
          >
            <PlusIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {canView && (
        <Table
          data={tenants as Tenant[]}
          columns={columns}
          actions={actions}
          searchable={true}
          onSearch={setSearch}
          pagination={true}
          isLoading={isLoading}
          maxHeight="67vh"
          infinite
          hasNextPage={hasNextPage}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
          selectable={canDelete}
          getRowId={(tenant) => tenant.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          bulkActions={
            canDelete
              ? [
                  {
                    label: "Delete Selected",
                    icon: <TrashIcon className="h-4 w-4" />,
                    onClick: handleBulkDelete,
                    variant: "danger" as const,
                  },
                ]
              : undefined
          }
        />
      )}

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCancel}
          title={selectedTenant ? "Edit Tenant" : "Create Tenant"}
          size="full"
        >
          <TenantForm
            initialData={selectedTenant || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={false}
            isUpdate={selectedTenant !== null}
            isOpen={isModalOpen}
          />
        </Modal>
      )}
    </div>
  );
};

export default TenantList;
